import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { RotateCcw, Trash2 } from "lucide-react";

const TYPE_LABEL = { entree: "Entrée", sortie: "Sortie", entree_sortie: "Entrée + sortie" };

export default function Corbeille() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purge, setPurge] = useState(null);

  const load = async () => { setLoading(true); setItems((await api.get("/corbeille")).data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const restaurer = async (it) => {
    try { await api.post(`/corbeille/${it.id}/restaurer`); toast.success("Constat restauré"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const purger = async () => {
    try { await api.delete(`/corbeille/${purge.id}`); toast.success("Supprimé définitivement"); setPurge(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="corbeille-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-dd-blue">Corbeille</h1>
        <p className="text-sm text-dd-warm mt-1">Les constats supprimés sont conservés 30 jours, puis effacés définitivement.</p>
      </div>
      <div className="bg-white rounded-lg border border-dd-light overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-dd-blue text-white text-left">
            <th className="px-4 py-3 font-semibold">Dossier</th><th className="px-4 py-3 font-semibold">Immeuble / objet</th>
            <th className="px-4 py-3 font-semibold">Type</th><th className="px-4 py-3 font-semibold">Supprimé</th>
            <th className="px-4 py-3 font-semibold">Effacement</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="px-4 py-10 text-center text-dd-warm">Chargement…</td></tr>
              : items.length === 0 ? <tr><td colSpan={6} className="px-4 py-16 text-center text-dd-warm"><Trash2 className="mx-auto mb-3 text-dd-light" size={40} />La corbeille est vide.</td></tr>
              : items.map((it) => (
                <tr key={it.id} className="border-t border-dd-light" data-testid={`corbeille-row-${it.id}`}>
                  <td className="px-4 py-3 font-medium text-dd-blue">{it.numero_dossier || "—"}</td>
                  <td className="px-4 py-3"><div className="text-dd-night">{it.immeuble || "—"}</div><div className="text-xs text-dd-warm">{it.locataire_entrant || it.locataire_sortant || ""}</div></td>
                  <td className="px-4 py-3">{TYPE_LABEL[it.type_constat]}</td>
                  <td className="px-4 py-3 text-dd-night">{new Date(it.supprime_le).toLocaleDateString("fr-CH")}<div className="text-xs text-dd-warm">{it.supprime_par}</div></td>
                  <td className="px-4 py-3"><Badge className={`border-0 ${it.jours_restants <= 5 ? "bg-destructive/10 text-destructive" : "bg-dd-light text-dd-night"}`}>dans {it.jours_restants} j</Badge></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => restaurer(it)} data-testid={`restaurer-${it.id}`} className="h-9 border-dd-blue text-dd-blue mr-2"><RotateCcw size={14} className="mr-1" /> Restaurer</Button>
                    {user?.role === "admin" && <Button size="sm" variant="outline" onClick={() => setPurge(it)} data-testid={`purger-${it.id}`} className="h-9 border-destructive text-destructive"><Trash2 size={14} className="mr-1" /> Effacer</Button>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <AlertDialog open={!!purge} onOpenChange={(o) => !o && setPurge(null)}>
        <AlertDialogContent data-testid="purge-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Effacer définitivement ce constat ?</AlertDialogTitle>
            <AlertDialogDescription>Le constat « {purge?.numero_dossier || purge?.immeuble} » sera supprimé sans possibilité de récupération.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11" data-testid="purge-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={purger} className="h-11 bg-destructive hover:bg-destructive/90 text-white" data-testid="purge-confirm">Effacer définitivement</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
